import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import PhoneInput from '../components/PhoneInput';
import { studentsAPI, settingsAPI, batchesAPI, uploadAPI, resolveImage, paymentsAPI , translationAPI} from '../utils/api';
import OrganizationPaymentDetailsWidget from '../components/OrganizationPaymentDetailsWidget';
import { useTranslation } from 'react-i18next';
import { tField } from '../utils/bilingualHelper';

import BilingualField from '../components/common/BilingualField';

function RegisterStudentModal({ onClose, onSave }) {
  const [step, setStep] = useState(1);
  const [loading, setLoad] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [batches, setBatches] = useState([]);
  const [levels, setLevels] = useState([]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [selectedFeeMonths, setSelectedFeeMonths] = useState([]);
  const [selectedMerchandise, setSelectedMerchandise] = useState([]); // Array of merchandise objects (with selectedSize if applicable)
  const [sizePromptData, setSizePromptData] = useState(null); // { merchandise: obj }
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptUrl, setReceiptUrl] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [declarationAgreed, setDeclarationAgreed] = useState(false);
  const [parentTranslating, setParentTranslating] = useState(false);
  const [parentTranslated, setParentTranslated] = useState(false);
  const [studentTranslating, setStudentTranslating] = useState(false);
  const [studentTranslated, setStudentTranslated] = useState(false);

  const [form, setForm] = useState({
    studentData: {
      name: { en: '', zh: '' },
      photoUrl: null,
      currentLevel: 'Beginner',
      dob: '',
      gender: 'Male',
      medicalNote: { en: '', zh: '' },
      trainingExperience: '',
      email: '',
      mobile: '+852 ',
      feeStatus: 'Pending',
      hkid: ''
    },
    parentData: { name: { en: '', zh: '' }, mobile: '+852 ', email: '', relationToStudent: 'Father', altMobile: '+852 ', address: { en: '', zh: '' } },
    batchId: '',
    paymentMethod: 'Cash',
    guardianPayerName: ''
  });



  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('photo', file);

    setUploading(true);
    try {
      const res = await uploadAPI.studentPhoto(formData);
      setForm(prev => ({
        ...prev,
        studentData: { ...prev.studentData, photoUrl: res.data.url }
      }));
    } catch (err) {
      alert('Photo upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleTranslateStudent = async () => {
    const fields = ['name', 'medicalNote'];
    const fieldsWithData = fields.filter(
      f => form.studentData[f]?.en?.trim() || form.studentData[f]?.zh?.trim()
    );
    if (fieldsWithData.length === 0) return setError('Please fill in at least one language for the student name before translating.');

    setStudentTranslating(true);
    setError('');
    try {
      const promises = fieldsWithData.map(f => {
        const enStr = form.studentData[f]?.en?.trim();
        const zhStr = form.studentData[f]?.zh?.trim();
        if (enStr && zhStr) return Promise.resolve({ field: f }); // both filled — skip
        if (zhStr && !enStr) {
          return translationAPI.translate(zhStr, 'zh-TW', 'en')
            .then(d => ({ field: f, en: d.responseData?.translatedText || '' }))
            .catch(() => ({ field: f }));
        }
        return translationAPI.translate(enStr, 'en', 'zh-TW')
          .then(d => ({ field: f, zh: d.responseData?.translatedText || '' }))
          .catch(() => ({ field: f }));
      });
      const results = await Promise.all(promises);
      const updates = {};
      results.forEach(res => {
        if (res.zh) updates[res.field] = { ...form.studentData[res.field], zh: res.zh };
        if (res.en) updates[res.field] = { ...form.studentData[res.field], en: res.en };
      });
      setForm(p => ({ ...p, studentData: { ...p.studentData, ...updates } }));
      setStudentTranslated(true);
    } catch { setError('Translation failed. Please try again.'); }
    finally { setStudentTranslating(false); }
  };

  const handleTranslateParent = async () => {
    const fields = ['name', 'address'];
    const fieldsWithData = fields.filter(
      f => form.parentData[f]?.en?.trim() || form.parentData[f]?.zh?.trim()
    );
    if (fieldsWithData.length === 0) return setError('Please fill in at least one language for Name or Address before translating.');

    setParentTranslating(true);
    setError('');
    try {
      const promises = fieldsWithData.map(f => {
        const enStr = form.parentData[f]?.en?.trim();
        const zhStr = form.parentData[f]?.zh?.trim();
        // Skip if both already filled
        if (enStr && zhStr) return Promise.resolve({ field: f });
        if (zhStr && !enStr) {
          return translationAPI.translate(zhStr, 'zh-TW', 'en')
            .then(d => ({ field: f, en: d.responseData?.translatedText || '' }))
            .catch(() => ({ field: f }));
        }
        return translationAPI.translate(enStr, 'en', 'zh-TW')
          .then(d => ({ field: f, zh: d.responseData?.translatedText || '' }))
          .catch(() => ({ field: f }));
      });
      const results = await Promise.all(promises);
      const updates = {};
      results.forEach(res => {
        if (res.zh) updates[res.field] = { ...form.parentData[res.field], zh: res.zh };
        if (res.en) updates[res.field] = { ...form.parentData[res.field], en: res.en };
      });
      setForm(p => ({ ...p, parentData: { ...p.parentData, ...updates } }));
      setParentTranslated(true);
    } catch { setError('Translation failed. Please try again.'); }
    finally { setParentTranslating(false); }
  };

  const handleReceiptUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setReceiptFile(file);
    const formData = new FormData();
    formData.append('receipt', file);

    setUploadingReceipt(true);
    try {
      const res = await uploadAPI.paymentReceipt(formData);
      setReceiptUrl(res.data.url);
    } catch (err) {
      alert('Receipt upload failed. Please try again.');
      setReceiptFile(null);
    } finally {
      setUploadingReceipt(false);
    }
  };

  const getAge = (dob) => {
    if (!dob) return 0;
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  useEffect(() => {
    Promise.all([batchesAPI.getAll(), settingsAPI.get()])
      .then(([bRes, sRes]) => {
        setBatches(bRes.data);
        setLevels(sRes.data.masterLevels);
      })
      .catch(console.error);
  }, []);

  // Helper: generate month strings from a batch's startDate → endDate
  const getBatchMonthsForReg = (batch) => {
    if (!batch) return [];
    const CURRENT = new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    if (!batch.startDate && !batch.endDate) return [CURRENT];
    const startD = batch.startDate ? new Date(batch.startDate) : new Date();
    const endD = batch.endDate
      ? new Date(batch.endDate)
      : (() => { const d = new Date(startD); d.setFullYear(d.getFullYear() + 1); return d; })();
    const list = [];
    const d = new Date(startD);
    d.setDate(1);
    let count = 0;
    while (d <= endD && count < 36) {
      list.push(d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }));
      d.setMonth(d.getMonth() + 1);
      count++;
    }
    return list.length > 0 ? list : [CURRENT];
  };

  // Derived: selected batch object + its months
  const selectedBatchObj = useMemo(() => batches.find(b => b._id === form.batchId) || null, [batches, form.batchId]);
  const batchMonthsForReg = useMemo(() => getBatchMonthsForReg(selectedBatchObj), [selectedBatchObj]);
  const CURRENT_MONTH_STR = new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

  const toggleFeeMonth = (month) => {
    setSelectedFeeMonths(prev =>
      prev.includes(month) ? prev.filter(m => m !== month) : [...prev, month]
    );
  };

  const getMerchandiseId = (m) => m?.id || m?._id || '';

  const toggleMerchandise = (m) => {
    const mId = getMerchandiseId(m);
    if (!mId) return;
    
    const isSelected = selectedMerchandise.some(item => getMerchandiseId(item) === mId);
    if (isSelected) {
      setSelectedMerchandise(prev => prev.filter(item => getMerchandiseId(item) !== mId));
    } else {
      if (m.sizes && m.sizes.length > 0) {
        setSizePromptData({ merchandise: m });
      } else {
        setSelectedMerchandise(prev => [...prev, m]);
      }
    }
  };

  const confirmMerchandiseSize = (size) => {
    if (sizePromptData && sizePromptData.merchandise) {
      setSelectedMerchandise(prev => [...prev, { ...sizePromptData.merchandise, selectedSize: size }]);
    }
    setSizePromptData(null);
  };

  const feePerMonth = selectedBatchObj?.feeAmount || 1800;
  const merchandiseFee = selectedMerchandise.reduce((acc, m) => acc + m.price, 0);
  const totalFeeAmount = (feePerMonth * selectedFeeMonths.length) + merchandiseFee;

  const validateStep = (targetStep = step) => {
    setError('');
    if (targetStep === 1 || targetStep === 'all') {
      if (!form.studentData.name?.en?.trim()) return "Student name is required.";
      if (form.studentData.name?.en?.trim().length < 3) return "Please enter a valid full name.";
      if (!form.studentData.dob) return "Student date of birth is required.";

      // Require bilingual translation before proceeding from Step 1
      if (!form.studentData.name?.zh?.trim()) {
        return "Please click ✨ Translate All to translate the student name to Chinese before proceeding.";
      }

      const age = getAge(form.studentData.dob);
      if (age >= 11) {
        if (!form.studentData.hkid || !form.studentData.hkid.trim()) {
          return "HKID is required for students aged 11 and above.";
        }
        const hkidRegex = /^[A-Z]{1,2}[0-9]{6}\(?[0-9A]\)?$/i;
        if (!hkidRegex.test(form.studentData.hkid.trim())) {
          return "Please enter a valid HKID (e.g., A123456(3) or A1234563).";
        }
      }

      if (!form.studentData.email.trim() || !form.studentData.email.includes('@')) {
        return "Student Gmail is mandatory for registration.";
      }

      if (age >= 18 && !form.studentData.mobile.trim()) {
        return "Student contact number is mandatory for students aged 18 or above.";
      }
    }
    if (targetStep === 2 || targetStep === 'all') {
      if (!form.parentData.name?.en?.trim()) return "Guardian name is required.";
      const mobileClean = form.parentData.mobile.replace(/\D/g, '');
      if (mobileClean.length < 8) return "Valid mobile number is required for login.";
      if (form.parentData.email && !form.parentData.email.includes('@')) return "Please enter a valid email address.";

      const studentEmail = (form.studentData.email || '').trim().toLowerCase();
      const parentEmail = (form.parentData.email || '').trim().toLowerCase();
      if (studentEmail && parentEmail && studentEmail === parentEmail) {
        return "Guardian email cannot be identical to the Student's login email.";
      }
      const studentMobileClean = (form.studentData.mobile || '').replace(/\D/g, '').slice(-8);
      const parentMobileClean = mobileClean.slice(-8);
      if (studentMobileClean && parentMobileClean && studentMobileClean === parentMobileClean) {
        return "Guardian mobile cannot be identical to the Student's mobile number.";
      }

      // Require bilingual translation before proceeding
      if (!form.parentData.name?.zh?.trim()) {
        return "Please click ✨ Translate All to translate the guardian name to Chinese before proceeding.";
      }
    }
    if (targetStep === 3 || targetStep === 'all') {
      if (!form.batchId) return "Please select a starting batch for the student.";
      const missingSize = selectedMerchandise.find(m => m.sizes && m.sizes.length > 0 && !m.selectedSize);
      if (missingSize) {
        const mNameStr = typeof missingSize.name === 'object' ? (missingSize.name.en || missingSize.name.zh || 'item') : String(missingSize.name || 'item');
        return `Please select a size for merchandise: ${mNameStr}.`;
      }
      if ((selectedFeeMonths.length > 0 || merchandiseFee > 0) && form.paymentMethod === 'Bank/QR Transfer') {
        if (!receiptUrl && !receiptFile) {
          return "Please upload a payment receipt for Bank/QR Transfer.";
        }
        if (uploadingReceipt) {
          return "Please wait for the payment receipt to finish uploading.";
        }
      }
    }
    if (targetStep === 4 || targetStep === 'all') {
      if (!declarationAgreed) {
        return "You must acknowledge and agree to the mandatory data collection purposes.";
      }
    }
    return null;
  };

  const handleNext = () => {
    const err = validateStep(step);
    if (err) return setError(err);
    setStep(step + 1);
  };

  const handleRegister = async () => {
    const err = validateStep('all');
    if (err) return setError(err);

    setLoad(true);
    try {
      const payload = {
        ...form,
        studentData: {
          ...form.studentData,
          purchasedMerchandise: selectedMerchandise.map(m => ({
            merchandise: getMerchandiseId(m),
            priceAtPurchase: m.price,
            size: m.selectedSize || undefined
          }))
        }
      };
      const res = await studentsAPI.create(payload);
      const createdStudent = res.data.student;

      // If admin selected months to pay upfront, or if there's a merchandise fee
      if ((selectedFeeMonths.length > 0 || merchandiseFee > 0) && createdStudent?.studentId) {
        try {
          await paymentsAPI.submitMultiple({
            studentId: createdStudent.studentId,
            amountPerMonth: feePerMonth,
            months: selectedFeeMonths,
            merchandiseFee: merchandiseFee,
            paymentMethod: form.paymentMethod === 'Bank/QR Transfer' ? 'QR/Transfer' : (form.paymentMethod || 'Cash'),
            paymentType: 'Monthly Fee',
            paidBy: 'Parent/Guardian',
            guardianName: form.guardianPayerName || form.parentData.name?.en || 'Admin Entry',
            batchId: form.batchId || null,
            receiptUrl: receiptUrl || null
          });
        } catch (payErr) {
          console.error('[REG_PAYMENT_ERROR]', payErr);
          // Don't block registration — payment can be re-recorded manually
        }
      }

      setResult(res.data);
      onSave();
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed.');
    } finally {
      setLoad(false);
    }
  };

  const handleWhatsAppShare = () => {
    const parentMobile = form.parentData.mobile;
    const studentName = tField(result.student?.name) || '';
    const parentName = tField(result.parent?.name) || tField(form.parentData?.name) || '';
    const studentId = result.credentials.studentId;
    const password = result.credentials.password;

    const message = encodeURIComponent(
      `Welcome to Academix! 🩰\n\n` +
      `We have successfully registered *${studentName}* for their dance journey.\n\n` +
      `*Your Login Credentials:*\n` +
      `• Parent Name: ${parentName}\n` +
      `• Parent Login: ${parentMobile}\n` +
      `• Student ID: ${studentId}\n` +
      `• Temporary Password: ${password}\n\n` +
      `You can now log in to the Academix Portal or Mobile App to track attendance and progress. See you in class!`
    );

    window.open(`https://wa.me/${parentMobile.replace(/\D/g, '')}?text=${message}`, '_blank');
  };

  if (result) {
    return (
      <div className="dash-modal-overlay" style={{ backdropFilter: 'blur(12px)', backgroundColor: 'rgba(123, 29, 58, 0.3)' }}>
        <div className="dash-modal animate-scale-in" style={{ maxWidth: 480, textAlign: 'center', padding: 40, borderRadius: 24, border: '1px solid rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.95)' }}>
          <div className="success-lottie-placeholder" style={{ fontSize: 64, marginBottom: 20 }}>👑</div>
          <h2 className="dash-modal-title" style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, marginBottom: 12 }}>Account Provisioned</h2>
          <p className="text-muted mb-8" style={{ fontSize: 16 }}>The family profile has been synced. Here is the universal registration pass:</p>

          <div className="premium-card-glass mb-8" style={{
            background: 'linear-gradient(135deg, var(--black) 0%, #333 100%)',
            color: '#fff',
            padding: 24,
            borderRadius: 16,
            textAlign: 'left',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
          }}>
            <div style={{ position: 'absolute', top: -20, right: -20, width: 100, height: 100, background: 'var(--wine)', filter: 'blur(40px)', opacity: 0.6 }}></div>
            <div style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.6, marginBottom: 16 }}>Credentials Receipt</div>

            {/* Student name */}
            <div className="mb-4">
              <div style={{ fontSize: 11, opacity: 0.6, textTransform: 'uppercase', marginBottom: 2 }}>Student</div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>
                {tField(result.student?.name) || '—'}
              </div>
            </div>

            {/* Parent name */}
            <div className="mb-4">
              <div style={{ fontSize: 11, opacity: 0.6, textTransform: 'uppercase', marginBottom: 2 }}>Parent / Guardian</div>
              <div style={{ fontSize: 15, fontWeight: 500 }}>
                {tField(result.parent?.name) || tField(form.parentData?.name) || '—'}
              </div>
            </div>

            <div className="mb-4">
              <div style={{ fontSize: 11, opacity: 0.6, textTransform: 'uppercase', marginBottom: 2 }}>Student Access Key</div>
              <div style={{ fontSize: 18, color: 'var(--wine-light)', fontWeight: 700, letterSpacing: 1 }}>{result.credentials.studentId}</div>
            </div>
            <div className="mb-4">
              <div style={{ fontSize: 11, opacity: 0.6, textTransform: 'uppercase', marginBottom: 2 }}>Parent Login ID</div>
              <div style={{ fontSize: 18, fontWeight: 500 }}>{result.credentials.parentId}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, opacity: 0.6, textTransform: 'uppercase', marginBottom: 2 }}>Temporary Password</div>
              <div style={{ fontSize: 14, fontFamily: 'monospace', background: 'rgba(255,255,255,0.1)', padding: '2px 8px', borderRadius: 4 }}>{result.credentials.password}</div>
            </div>

            <div style={{ position: 'absolute', bottom: 20, right: 20, opacity: 0.1, fontSize: 40 }}>⚖️</div>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <button className="btn btn-outline w-full" style={{ padding: 14, borderRadius: 12, border: '2px solid #25D366', color: '#25D366' }} onClick={handleWhatsAppShare}>
              Share via WhatsApp
            </button>
            <button className="btn btn-primary w-full" style={{ padding: 14, borderRadius: 12 }} onClick={onClose}>Finish & Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dash-modal-overlay" style={{ backdropFilter: 'blur(8px)', backgroundColor: 'rgba(0,0,0,0.4)', padding: 0 }}>
      <div className="dash-modal animate-slide-up" style={{ maxWidth: '100vw', width: '100vw', height: '100vh', maxHeight: '100vh', display: 'flex', flexDirection: 'column', borderRadius: 0, overflow: 'hidden', border: 'none', boxShadow: 'none' }}>

        {/* Premium Header */}
        <div style={{ background: 'var(--black)', padding: '32px 40px', color: '#fff' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 700 }}>Register New Family</h2>
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>Atomic Provisioning Engine v1.5</p>
            </div>
            <button style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: 8, cursor: 'pointer' }} onClick={onClose}>✕</button>
          </div>

          {/* Premium Stepper */}
          <div style={{ display: 'flex', marginTop: 32, position: 'relative', justifyContent: 'space-between' }}>
            <div style={{ position: 'absolute', top: 12, left: 0, right: 0, height: 2, background: 'rgba(255,255,255,0.1)', zIndex: 0 }}></div>
            <div style={{ position: 'absolute', top: 12, left: 0, width: `${((step - 1) / 3) * 100}%`, height: 2, background: 'var(--wine-light)', zIndex: 1, transition: 'width .4s ease' }}></div>

            {[1, 2, 3, 4].map(s => {
              const reachable = s === 1 || form.studentData.name?.en;
              return (
                <div
                  key={s}
                  style={{ zIndex: 2, position: 'relative', textAlign: 'center', cursor: reachable ? 'pointer' : 'not-allowed' }}
                  onClick={() => {
                    if (reachable) {
                      setError('');
                      setStep(s);
                    }
                  }}
                >
                  <div style={{
                    width: 26, height: 26, borderRadius: '50%', background: step >= s ? 'var(--wine-light)' : '#333',
                    color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto', border: '4px solid var(--black)', transition: 'background .4s'
                  }}>
                    {step > s ? '✓' : s}
                  </div>
                  <div style={{ fontSize: 10, fontWeight: 600, marginTop: 8, color: step >= s ? '#fff' : 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: 1 }}>
                    {s === 1 ? 'Student' : s === 2 ? 'Guardian' : s === 3 ? 'Placement' : 'Declaration'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="dash-modal-body" style={{ padding: '40px', background: '#fff', overflowY: 'auto', flex: 1 }}>
          {error && <div className="alert alert-error mb-4">{error}</div>}

          {step === 1 && (
            <div className="animate-fade-in">
              {/* Profile Photo Uploader */}
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
                <div style={{ position: 'relative' }}>
                  <div
                    onClick={() => document.getElementById('student-photo-input').click()}
                    style={{
                      width: 100,
                      height: 100,
                      borderRadius: '50%',
                      backgroundColor: '#f8f9fa',
                      border: '2px dashed #ddd',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      overflow: 'hidden',
                      position: 'relative',
                      backgroundImage: form.studentData.photoUrl ? `url("${resolveImage(form.studentData.photoUrl)}")` : 'none',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  >
                    {!form.studentData.photoUrl && !uploading && (
                      <>
                        <span style={{ fontSize: 24 }}>👤</span>
                        <span style={{ fontSize: 10, color: '#999', marginTop: 4 }}>UPLOAD PHOTO</span>
                      </>
                    )}
                    {uploading && <div style={{ fontSize: 10, color: 'var(--wine)' }}>UPLOADING...</div>}
                  </div>
                  <input
                    id="student-photo-input"
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handlePhotoUpload}
                  />
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    background: 'var(--wine)',
                    color: '#fff',
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    border: '3px solid #fff'
                  }}>+</div>
                </div>
              </div>

              <div className="form-group mb-6">
                <label className="form-label-style" style={{ fontSize: 11, color: 'var(--mid-grey)' }}>FULL LEGAL NAME</label>
                <BilingualField label="" value={form.studentData.name} onChange={(_, lang, val) => { setError(''); setStudentTranslated(false); setForm(p => ({ ...p, studentData: { ...p.studentData, name: { ...p.studentData.name, [lang]: val } } })); }} placeholder="Enter student name..." />
              </div>
              <div className="form-row-2 mb-6">
                <div className="form-group">
                  <label className="form-label-style">
                    DATE OF BIRTH {form.studentData.dob && <span style={{color: 'var(--wine)', marginLeft: 8, fontWeight: 700}}>({getAge(form.studentData.dob)} yrs old)</span>}
                  </label>
                  <input
                    type="date" className="form-input-style" value={form.studentData.dob}
                    onChange={e => {
                      const val = e.target.value;
                      setForm(p => ({ ...p, studentData: { ...p.studentData, dob: val } }));
                    }}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label-style">GENDER</label>
                  <select
                    className="form-input-style" value={form.studentData.gender}
                    onChange={e => {
                      const val = e.target.value;
                      setForm(p => ({ ...p, studentData: { ...p.studentData, gender: val } }));
                    }}
                  >
                    <option>Male</option><option>Female</option><option>Other</option>
                  </select>
                </div>
              </div>

              {form.studentData.dob && getAge(form.studentData.dob) >= 11 && (
                <div className="form-group mb-6 animate-fade-in">
                  <label className="form-label-style">HKID NUMBER *</label>
                  <input
                    className="form-input-style"
                    value={form.studentData.hkid || ''}
                    onChange={e => {
                      const val = e.target.value.toUpperCase();
                      setForm(p => ({ ...p, studentData: { ...p.studentData, hkid: val } }));
                    }}
                    placeholder="e.g. A123456(3)"
                  />
                </div>
              )}
              <div className="form-group mb-6">
                <label className="form-label-style">YEARS OF EXPERIENCE</label>
                <input
                  type="number"
                  className="form-input-style"
                  value={form.studentData.trainingExperience}
                  onChange={e => {
                    const val = e.target.value;
                    setForm(p => ({ ...p, studentData: { ...p.studentData, trainingExperience: val } }));
                  }}
                  placeholder="Years of background (e.g. 2)"
                  min="0"
                />
              </div>
              <div className="form-group mb-6">
                <label className="form-label-style">MEDICAL NOTES & SAFETY</label>
                <BilingualField label="" value={form.studentData.medicalNote} onChange={(_, lang, val) => { setStudentTranslated(false); setForm(p => ({ ...p, studentData: { ...p.studentData, medicalNote: { ...p.studentData.medicalNote, [lang]: val } } })); }} placeholder="Allergies, chronic conditions or past injuries..." />
              </div>

              {/* ── Student Translate Button ── */}
              <div style={{ background: studentTranslated ? 'rgba(76,175,134,0.08)' : 'rgba(123,29,58,0.06)', border: `1px solid ${studentTranslated ? '#4caf86' : 'rgba(123,29,58,0.2)'}`, borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  {studentTranslated ? (
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#4caf86', marginBottom: 4 }}>✅ Chinese Translation Ready</div>
                      <div style={{ fontSize: 12, color: '#666' }}>
                        <span style={{ marginRight: 12 }}>Name ZH: <b>{form.studentData.name?.zh || '—'}</b></span>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#7b1d3a', marginBottom: 2 }}>🌐 Auto-Translate Student Info</div>
                      <div style={{ fontSize: 12, color: '#888' }}>Fill the name in one language, then click to translate.</div>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleTranslateStudent}
                  disabled={studentTranslating}
                  style={{ background: studentTranslated ? '#4caf86' : 'var(--wine)', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontWeight: 700, fontSize: 13, cursor: studentTranslating ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', opacity: studentTranslating ? 0.7 : 1, minWidth: 130 }}
                >
                  {studentTranslating ? '⏳ Translating...' : studentTranslated ? '🔄 Re-translate' : '✨ Translate All'}
                </button>
              </div>

              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label-style">STUDENT GMAIL *</label>
                  <input
                    className="form-input-style" value={form.studentData.email}
                    onChange={e => {
                      const val = e.target.value;
                      setForm(p => ({ ...p, studentData: { ...p.studentData, email: val } }));
                    }}
                    placeholder="student@gmail.com"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label-style">
                    {getAge(form.studentData.dob) >= 18 ? "STUDENT CONTACT *" : "STUDENT CONTACT (OPTIONAL)"}
                  </label>
                  <PhoneInput
                    value={form.studentData.mobile}
                    onChange={val => {
                      setForm(p => ({ ...p, studentData: { ...p.studentData, mobile: val } }));
                    }}
                    placeholder="8-digit local number"
                  />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="animate-fade-in">
              <div className="form-group mb-6">
                <label className="form-label-style">PRIMARY GUARDIAN NAME</label>
                <BilingualField label="" value={form.parentData.name} onChange={(_, lang, val) => { setError(''); setForm(p => ({ ...p, parentData: { ...p.parentData, name: { ...p.parentData.name, [lang]: val } } })); }} placeholder="Enter primary guardian name..." />
              </div>
              <div className="form-row-2 mb-6">
                <div className="form-group">
                  <label className="form-label-style">MOBILE (SYSTEM LOGIN)</label>
                  <PhoneInput
                    value={form.parentData.mobile}
                    onChange={val => {
                      setError('');
                      setForm(p => ({ ...p, parentData: { ...p.parentData, mobile: val } }));
                    }}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label-style">RELATION</label>
                  <select
                    className="form-input-style" value={form.parentData.relationToStudent}
                    onChange={e => {
                      const val = e.target.value;
                      setForm(p => ({ ...p, parentData: { ...p.parentData, relationToStudent: val } }));
                    }}
                  >
                    <option>Father</option><option>Mother</option><option>Legal Guardian</option>
                  </select>
                </div>
              </div>
              <div className="form-group mb-6">
                <label className="form-label-style">PRIMARY EMAIL</label>
                <input
                  className="form-input-style" value={form.parentData.email}
                  onChange={e => {
                    const val = e.target.value;
                    setForm(p => ({ ...p, parentData: { ...p.parentData, email: val } }));
                  }}
                  placeholder="For invoices and updates"
                />
              </div>
              <div className="form-group">
                <label className="form-label-style">RESIDENTIAL ADDRESS</label>
                <BilingualField label="" value={form.parentData.address} onChange={(_, lang, val) => { setParentTranslated(false); setForm(p => ({ ...p, parentData: { ...p.parentData, address: { ...p.parentData.address, [lang]: val } } })); }} placeholder="Street address, building, apartment..." />
              </div>

              {/* ── Guardian Translate Button ── */}
              <div style={{ background: parentTranslated ? 'rgba(76,175,134,0.08)' : 'rgba(123,29,58,0.06)', border: `1px solid ${parentTranslated ? '#4caf86' : 'rgba(123,29,58,0.2)'}`, borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
                <div style={{ flex: 1 }}>
                  {parentTranslated ? (
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#4caf86', marginBottom: 4 }}>✅ Chinese Translation Ready</div>
                      <div style={{ fontSize: 12, color: '#666' }}>
                        <span style={{ marginRight: 12 }}>Name ZH: <b>{form.parentData.name?.zh || '—'}</b></span>
                        <span>Address ZH: <b>{form.parentData.address?.zh || '—'}</b></span>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#7b1d3a', marginBottom: 2 }}>🌐 Auto-Translate Guardian Info</div>
                      <div style={{ fontSize: 12, color: '#888' }}>Fill name/address in one language, then click to translate.</div>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleTranslateParent}
                  disabled={parentTranslating}
                  style={{ background: parentTranslated ? '#4caf86' : 'var(--wine)', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontWeight: 700, fontSize: 13, cursor: parentTranslating ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', opacity: parentTranslating ? 0.7 : 1, minWidth: 130 }}
                >
                  {parentTranslating ? '⏳ Translating...' : parentTranslated ? '🔄 Re-translate' : '✨ Translate All'}
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="animate-fade-in">
              <div className="dash-card bg-light mb-8" style={{ border: '1px solid var(--border)', background: 'var(--off-white)' }}>
                <div className="p-5">
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--black)', marginBottom: 4 }}>Curriculum Placement</div>
                  <div style={{ fontSize: 12, opacity: 0.6 }}>The student will start their journey at this level.</div>
                  <div className="mt-4">
                    <select
                      className="form-input-style" style={{ background: '#fff' }} value={form.studentData.currentLevel}
                      onChange={e => {
                        const val = e.target.value;
                        setForm(p => ({ ...p, studentData: { ...p.studentData, currentLevel: val } }));
                      }}
                    >
                      {levels.map(l => <option key={l}>{l}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-group mb-6">
                <label className="form-label-style">CHOOSE STARTING BATCH *</label>
                <select
                  className="form-input-style" value={form.batchId}
                  onChange={e => {
                    const val = e.target.value;
                    setError('');
                    setForm(p => ({ ...p, batchId: val }));
                    // Pre-select current month when batch is chosen
                    const chosenBatch = batches.find(b => b._id === val);
                    const months = getBatchMonthsForReg(chosenBatch);
                    setSelectedFeeMonths(months.includes(CURRENT_MONTH_STR) ? [CURRENT_MONTH_STR] : months.length > 0 ? [months[0]] : []);
                  }}
                >
                  <option value="">-- No Active Selection --</option>
                  {batches.map(b => (
                    <option key={b._id} value={b._id}>{tField(b.name)} · {b.level} ({b.startTime})</option>
                  ))}
                </select>
                <div style={{ fontSize: 11, color: 'var(--mid-grey)', marginTop: 8 }}>Tutors will see the new student in the batch roster instantly.</div>
              </div>

              {selectedBatchObj?.offeredMerchandise && selectedBatchObj.offeredMerchandise.length > 0 && (
                <div className="form-group mb-6">
                  <label className="form-label-style">COMPLIMENTARY MERCHANDISE</label>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                    {selectedBatchObj.offeredMerchandise.filter(m => m).map(m => {
                      const mId = getMerchandiseId(m);
                      const selectedItem = selectedMerchandise.find(sm => getMerchandiseId(sm) === mId);
                      const isSelected = !!selectedItem;
                      return (
                        <button
                          key={m._id}
                          type="button"
                          className={`badge ${isSelected ? 'badge-gold' : ''}`}
                          style={{
                            cursor: 'pointer',
                            border: '1px solid #ccc',
                            background: isSelected ? undefined : '#f9f9f9',
                            color: isSelected ? undefined : '#666',
                            padding: '6px 12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                          onClick={() => toggleMerchandise(m)}
                        >
                          {m.imageUrl && (
                            <img 
                              src={resolveImage(m.imageUrl)} 
                              alt={tField(m.name)} 
                              style={{ width: 24, height: 24, borderRadius: 4, objectFit: 'cover' }} 
                            />
                          )}
                          <span>
                            {tField(m.name)} {isSelected && selectedItem.selectedSize ? `(Size: ${selectedItem.selectedSize}) ` : ''}(+HK$ {m.price})
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 8 }}>Select any items the student is purchasing upon enrollment.</div>
                </div>
              )}

              {/* Size Selection Overlay */}
              {sizePromptData && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ background: '#fff', padding: 24, borderRadius: 16, width: 300, boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}>
                    <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Select Size</h3>
                    {sizePromptData.merchandise.imageUrl && (
                      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                        <img 
                          src={resolveImage(sizePromptData.merchandise.imageUrl)} 
                          alt={typeof sizePromptData.merchandise.name === 'object' ? (sizePromptData.merchandise.name.en || sizePromptData.merchandise.name.zh || '') : String(sizePromptData.merchandise.name || '')}
                          style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 12 }}
                        />
                      </div>
                    )}
                    <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>Please select a size for <strong>{typeof sizePromptData.merchandise.name === 'object' ? (sizePromptData.merchandise.name.en || sizePromptData.merchandise.name.zh || '') : String(sizePromptData.merchandise.name || '')}</strong>:</p>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
                      {sizePromptData.merchandise.sizes.map((size, idx) => (
                        <button
                          key={typeof size === 'object' ? (size.en || size.zh || idx) : size}
                          onClick={() => confirmMerchandiseSize(typeof size === 'object' ? tField(size) : size)}
                          className="btn btn-outline btn-sm"
                          style={{ flex: '1 0 30%', padding: '10px 0' }}
                        >
                          {typeof size === 'object' ? tField(size) : size}
                        </button>
                      ))}
                    </div>
                    <button onClick={() => setSizePromptData(null)} className="btn btn-sm btn-outline w-full" style={{ border: 'none', background: '#f5f5f5' }}>Cancel</button>
                  </div>
                </div>
              )}

              {/* ── Fee Payment at Registration ── */}
              {form.batchId && (batchMonthsForReg.length > 0 || selectedMerchandise.length > 0) && (
                <div style={{ marginTop: 24, background: 'linear-gradient(135deg, #fdf8f0 0%, #fff9f2 100%)', border: '1px solid #f0e0c8', borderRadius: 12, padding: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                    <span style={{ fontSize: 18 }}>💳</span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--black)' }}>Record Fee Payment at Registration</div>
                      <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>Optional — select months if fee is collected now. HK$ {feePerMonth.toLocaleString()} / month</div>
                    </div>
                  </div>

                  {/* Month checkboxes */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 8,
                    maxHeight: 220,
                    overflowY: 'auto',
                    marginBottom: 16
                  }}>
                    {batchMonthsForReg.map(month => {
                      const checked = selectedFeeMonths.includes(month);
                      const isCurrent = month === CURRENT_MONTH_STR;
                      return (
                        <label
                          key={month}
                          onClick={() => toggleFeeMonth(month)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '8px 12px',
                            borderRadius: 8,
                            border: `2px solid ${checked ? 'var(--wine, #8B1D3B)' : '#e5e5e5'}`,
                            background: checked ? 'rgba(139,29,59,0.06)' : '#fff',
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: checked ? 700 : 400,
                            color: checked ? 'var(--wine, #8B1D3B)' : '#555',
                            transition: 'all 0.15s',
                            userSelect: 'none',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            readOnly
                            style={{ accentColor: 'var(--wine, #8B1D3B)', width: 14, height: 14 }}
                          />
                          <span>{month}</span>
                          {isCurrent && (
                            <span style={{ fontSize: 9, background: '#f0e6ea', color: 'var(--wine, #8B1D3B)', padding: '1px 5px', borderRadius: 4, marginLeft: 'auto', fontWeight: 700 }}>Current</span>
                          )}
                        </label>
                      );
                    })}
                  </div>

                  {/* Amount preview */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: selectedFeeMonths.length > 0 ? 'rgba(139,29,59,0.06)' : '#f8f8f8',
                    border: `1px solid ${selectedFeeMonths.length > 0 ? 'rgba(139,29,59,0.2)' : '#e5e5e5'}`,
                    borderRadius: 8,
                    padding: '10px 14px',
                    marginBottom: selectedFeeMonths.length > 0 ? 14 : 0,
                    transition: 'all 0.2s'
                  }}>
                    <span style={{ fontSize: 13, color: '#666' }}>
                      {selectedFeeMonths.length > 0
                        ? `${selectedFeeMonths.length} month(s) × HK$ ${feePerMonth.toLocaleString()}`
                        : 'No months selected.'}
                      {merchandiseFee > 0 ? ` + Merchandise HK$ ${merchandiseFee}` : ''}
                    </span>
                    <span style={{ fontSize: 20, fontWeight: 800, color: 'var(--wine, #8B1D3B)' }}>
                      HK$ {totalFeeAmount.toLocaleString()}
                    </span>
                  </div>

                  {/* Payment method + payer name — only shown when amount > 0 */}
                  {(selectedFeeMonths.length > 0 || merchandiseFee > 0) && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div>
                        <label className="form-label-style" style={{ fontSize: 10 }}>PAYMENT METHOD</label>
                        <select
                          className="form-input-style"
                          style={{ background: '#fff', fontSize: 13 }}
                          value={form.paymentMethod}
                          onChange={e => setForm(p => ({ ...p, paymentMethod: e.target.value }))}
                        >
                          <option value="Cash">Cash</option>
                          <option value="Bank/QR Transfer">Bank / QR Transfer</option>
                        </select>
                      </div>
                      <div>
                        <label className="form-label-style" style={{ fontSize: 10 }}>RECEIVED FROM</label>
                        <input
                          className="form-input-style"
                          style={{ background: '#fff', fontSize: 13 }}
                          placeholder={form.parentData.name?.en || 'Guardian name...'}
                          value={form.guardianPayerName}
                          onChange={e => setForm(p => ({ ...p, guardianPayerName: e.target.value }))}
                        />
                      </div>
                    </div>
                  )}

                  {form.paymentMethod === 'Bank/QR Transfer' && (
                    <div style={{ marginTop: '16px' }}>
                      <OrganizationPaymentDetailsWidget containerStyle={{ margin: 0, boxShadow: 'none', border: '1px solid #eaeaea' }} />
                      <div className="form-group" style={{ marginTop: 16 }}>
                        <label className="form-label-style">UPLOAD PAYMENT RECEIPT <span style={{ color: 'red' }}>*</span></label>
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          className="form-input-style"
                          style={{ padding: '8px', background: '#fff' }}
                          onChange={handleReceiptUpload}
                          disabled={uploadingReceipt}
                        />
                        {uploadingReceipt && (
                          <div style={{ fontSize: 12, color: '#d97706', marginTop: 4, fontWeight: 600 }}>
                            ⏳ Uploading receipt...
                          </div>
                        )}
                        {receiptFile && !uploadingReceipt && (
                          <div style={{ fontSize: 12, color: '#2d7a52', marginTop: 4, fontWeight: 600 }}>
                            ✅ {receiptFile.name} attached
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="form-group mt-6">
                <label className="form-label-style">INITIAL PAYMENT STATUS</label>
                <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                  {['Pending', 'Paid'].map(s => (
                    <button
                      key={s}
                      type="button"
                      className={`btn btn-sm ${form.studentData.feeStatus === s ? 'btn-primary' : 'btn-outline'}`}
                      style={{
                        flex: 1,
                        borderRadius: 12,
                        padding: '14px',
                        borderColor: form.studentData.feeStatus === s ? 'var(--wine)' : 'var(--border)',
                        background: form.studentData.feeStatus === s ? 'var(--wine)' : '#fff',
                        color: form.studentData.feeStatus === s ? '#fff' : 'var(--black)',
                        fontWeight: 600,
                        transition: 'all .3s ease'
                      }}
                      onClick={() => setForm(p => ({ ...p, studentData: { ...p.studentData, feeStatus: s } }))}
                    >
                      {s === 'Paid' ? '🟢 Confirmed Paid' : '🟡 Payment Pending'}
                    </button>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: 'var(--mid-grey)', marginTop: 10 }}>Mark as Paid if you have received cash or transfer today.</div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="animate-fade-in">
              <div className="dash-card bg-light mb-8" style={{ border: '1px solid var(--border)', background: 'var(--off-white)', padding: 24 }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--black)', marginBottom: 16 }}>Personal Data Collection and Image Use Declaration</h3>
                
                <div style={{ marginBottom: 20 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 700, color: 'var(--black)', marginBottom: 8 }}>Part A – Mandatory Purposes</h4>
                  <p style={{ fontSize: 13, color: '#555', lineHeight: 1.5, marginBottom: 8 }}>
                    The name, date of birth, emergency contact details and other personal data collected by our school will be used for enrolment procedures, class allocation, student record keeping and internal administrative purposes.
                  </p>
                  <p style={{ fontSize: 13, color: '#555', lineHeight: 1.5, marginBottom: 8 }}>
                    When students participate in external performances or competitions, our school will provide the necessary information (such as name and age) to the event organiser for identity verification only. Parent/guardian contact details will not be provided.
                  </p>
                  <p style={{ fontSize: 13, color: '#555', lineHeight: 1.5, fontWeight: 600 }}>
                    This section is mandatory for the provision of services.
                  </p>
                </div>

                <hr style={{ border: 0, borderTop: '1px solid #e0e0e0', margin: '20px 0' }} />

                <div style={{ marginBottom: 24 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 700, color: 'var(--black)', marginBottom: 8 }}>Part B – Promotional and Publicity Purposes</h4>
                  <div style={{ fontSize: 13, color: 'var(--wine)', fontWeight: 600, marginBottom: 12 }}>(Voluntary; does not affect enrolment or performance opportunities)</div>
                  <ul style={{ fontSize: 13, color: '#555', lineHeight: 1.5, paddingLeft: 20, margin: 0 }}>
                    <li style={{ marginBottom: 6 }}>Photos/videos of students taken by instructors during classes, rehearsals or performances may be used on our school's official platforms, including Facebook, Instagram, YouTube and Xiaohongshu (RED).</li>
                    <li style={{ marginBottom: 6 }}>Photos of students may be used on our school's official website, promotional leaflets, posters or yearbooks.</li>
                    <li>Instructors may share photos/videos of class activities featuring students in our school's official WhatsApp parent group.</li>
                  </ul>
                </div>

                <div style={{ padding: '16px', background: 'rgba(139,29,59,0.06)', borderRadius: '8px', border: '1px solid rgba(139,29,59,0.2)' }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
                    <input 
                      type="checkbox" 
                      checked={declarationAgreed}
                      onChange={e => {
                        setDeclarationAgreed(e.target.checked);
                        if (e.target.checked) setError('');
                      }}
                      style={{ marginTop: 2, marginRight: 12, accentColor: 'var(--wine)', width: 16, height: 16 }}
                    />
                    <div>
                      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--black)', display: 'block' }}>I acknowledge and agree to the above mandatory purposes.</span>
                      <span style={{ fontSize: 12, color: 'var(--wine)', fontWeight: 500 }}>(Must be ticked, otherwise enrolment cannot be processed)</span>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="dash-modal-footer" style={{ padding: '30px 40px', background: '#fcfcfc', borderTop: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {step > 1 ? (
                <button className="btn btn-outline" style={{ border: 'none', background: 'transparent' }} onClick={() => { setError(''); setStep(step - 1); }}>← Previous Information</button>
              ) : (
                <button className="btn btn-outline" style={{ border: 'none', background: 'transparent' }} onClick={onClose}>Abandon Registration</button>
              )}
            </div>

            <div style={{ textAlign: 'right' }}>
              {error && <div style={{ color: 'var(--wine)', fontSize: 12, fontWeight: 600, marginBottom: 8 }} className="animate-fade-in">⚠️ {error}</div>}

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                {step < 4 ? (
                  <button
                    className="btn btn-primary"
                    style={{ padding: '12px 32px', borderRadius: 10 }}
                    onClick={handleNext}
                  >
                    Save & Continue
                  </button>
                ) : (
                  <button
                    className="btn btn-primary"
                    style={{ padding: '12px 32px', borderRadius: 10, background: 'var(--wine)' }}
                    onClick={handleRegister}
                    disabled={loading}
                  >
                    {loading ? 'Syncing Profile...' : 'Authorize Registration'}
                  </button>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

export default function StudentHub() {
  const { t } = useTranslation();
  const [students, setStudents] = useState([]);
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [showPromote, setShowPromote] = useState(null);
  const [showRegister, setShowRegister] = useState(false);
  const [newLevel, setNewLevel] = useState('');
  const navigate = useNavigate();

  const fetchIdRef = React.useRef(0);

  const load = useCallback((isMounted = true) => {
    const currentId = ++fetchIdRef.current;
    setLoading(true);
    setError('');
    Promise.all([studentsAPI.getAll(), settingsAPI.get()])
      .then(([sRes, lRes]) => {
        if (isMounted && currentId === fetchIdRef.current) {
          setStudents(Array.isArray(sRes.data) ? sRes.data : []);
          setLevels(lRes.data.masterLevels || []);
        }
      })
      .catch(err => {
        if (isMounted && currentId === fetchIdRef.current) {
          setError(err.response?.data?.message || t('students.errLoad'));
        }
      })
      .finally(() => {
        if (isMounted && currentId === fetchIdRef.current) setLoading(false);
      });
  }, [t]);

  useEffect(() => {
    let isMounted = true;
    load(isMounted);
    return () => { isMounted = false; };
  }, [load]);

  const handlePromote = async () => {
    if (!showPromote || !newLevel) return;
    try {
      await studentsAPI.promote(showPromote._id, showPromote.currentLevel, newLevel);
      load();
      setShowPromote(null);
    } catch { alert(t('students.promoteFailed')); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('students.deleteConfirm'))) return;
    try {
      await studentsAPI.delete(id);
      load();
    } catch { alert(t('students.deleteFailed')); }
  };

  const filtered = students.filter(s => {
    const nameStr = typeof s.name === 'object' && s.name !== null
      ? `${s.name.en || ''} ${s.name.zh || ''}`.trim()
      : String(s.name || '');
    const q = filter.toLowerCase();
    return nameStr.toLowerCase().includes(q) || s.studentId?.toLowerCase().includes(q);
  });

  return (
    <Layout title={t('students.title')}>
      {showRegister && <RegisterStudentModal onClose={() => setShowRegister(false)} onSave={load} />}

      <div className="dash-page-header">
        <div>
          <h1 className="dash-page-title">{t('students.headerTitle')}</h1>
          <p className="text-sm text-muted">{t('students.headerSub')}</p>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <input
            type="text" placeholder={t('students.search')} className="form-input-style"
            style={{ width: 240 }} value={filter} onChange={e => setFilter(e.target.value)}
          />
          <button className="btn btn-primary btn-sm" onClick={() => setShowRegister(true)}>{t('students.registerBtn')}</button>
        </div>
      </div>

      {loading ? <div className="p-8 text-center text-muted">⏳ {t('students.syncing')}</div> : error ? (
        <div className="dash-empty" style={{ padding: '60px 20px' }}>
          <div className="dash-empty-icon">⚠️</div>
          <div className="dash-empty-title">{t('students.errLoad')}</div>
          <p className="dash-empty-sub">{error}</p>
          <button className="btn btn-primary btn-sm" onClick={load} style={{ marginTop: 16 }}>{t('students.retry')}</button>
        </div>
      ) : (
        <div className="dash-card p-0" style={{ overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9f9f9', borderBottom: '1px solid var(--border)' }}>
                <th style={{ padding: 16, textAlign: 'left' }}>{t('students.thStudent')}</th>
                <th style={{ padding: 16, textAlign: 'left' }}>{t('students.thLevel')}</th>
                <th style={{ padding: 16, textAlign: 'left' }}>{t('students.thStatus')}</th>
                <th style={{ padding: 16, textAlign: 'right' }}>{t('students.thActions')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => (
                <tr key={s._id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div className="dash-avatar" style={{
                        width: 32,
                        height: 32,
                        backgroundColor: 'rgba(123, 29, 58, 0.1)',
                        color: 'var(--wine)',
                        backgroundImage: s.photoUrl ? `url("${resolveImage(s.photoUrl)}")` : 'none',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        fontWeight: 700,
                        overflow: 'hidden'
                      }}>
                        {!s.photoUrl && (typeof s.name === 'object' ? (s.name.en || s.name.zh || '?')[0] : String(s.name || '?')[0])}
                      </div>
                      <div>
                        <div className="font-bold">{tField(s.name)}</div>
                        <div className="text-xs text-muted">{s.studentId || t('students.noId')}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: 16 }}><span className="badge badge-gold">{s.currentLevel || t('students.beginner')}</span></td>
                  <td style={{ padding: 16 }}>
                    <span className={`badge ${s.userId?.status === 'inactive' ? 'badge-danger' : 'badge-success'}`}>
                      {s.userId?.status === 'inactive' ? t('students.deactivated') : t('students.active')}
                    </span>
                  </td>
                  <td style={{ padding: 16, textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button className="btn btn-xs btn-outline" onClick={() => setShowPromote(s)}>{t('students.promote')}</button>
                      <button className="btn btn-xs btn-primary" onClick={() => navigate(`/students/${s._id}`)}>{t('students.profile')}</button>
                      <button className="btn btn-xs btn-outline text-danger" onClick={() => handleDelete(s._id)}>✕</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {students.length === 0 ? (
            <div className="dash-empty" style={{ padding: '60px 20px' }}>
              <div className="dash-empty-icon">👨‍🎓</div>
              <div className="dash-empty-title">{t('students.noStudents')}</div>
              <p className="dash-empty-sub">{t('students.clickToRegister')}</p>
            </div>
          ) : !filtered.length && (
            <div className="p-12 text-center text-muted">{t('students.noMatch')}</div>
          )}
        </div>
      )}

      {showPromote && (
        <div className="dash-modal-overlay">
          <div className="dash-modal" style={{ maxWidth: 400 }}>
            <div className="dash-modal-header">
              <div className="dash-modal-title">{t('students.promoteTitle')}</div>
              <button className="dash-modal-close" onClick={() => setShowPromote(null)}>×</button>
            </div>
            <div className="dash-modal-body">
              <div className="form-group">
                <label className="form-label-style">{t('students.newLevel')}</label>
                <select className="form-input-style" value={newLevel} onChange={e => setNewLevel(e.target.value)}>
                  <option value="">{t('students.chooseLevel')}</option>
                  {(levels || []).map(l => <option key={l} value={l} disabled={l === showPromote.currentLevel}>{l}</option>)}
                </select>
              </div>
            </div>
            <div className="dash-modal-footer">
              <button className="btn btn-outline btn-sm" onClick={() => setShowPromote(null)}>{t('students.cancel')}</button>
              <button className="btn btn-primary btn-sm" onClick={handlePromote} disabled={!newLevel}>{t('students.promote')}</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
